/* ============================================================================
   ⛔ CHECKOUT RESCUE — 10 Aug 2026, launch day.

   Owner: *"i still cant check out"*, in two browsers, with the button at full
   colour (so NOT the minimum-order rule, which fades it). And then the thing
   that matters most: *"my customer cant check out, they dont know that they
   need that link"*. A workaround only I know about is not a fix.

   The button is `type="submit"` on the cart form, and I proved the form itself
   reaches checkout — from my own session, and by POSTing `checkout=` to /cart
   and watching the server issue the redirect. But something in his page stops
   the submit, and I cannot reproduce it, so I cannot fix the cause tonight.

   This makes the outcome not depend on the cause. Click the checkout button;
   if the page has not started leaving shortly afterwards, go to /checkout
   directly — the same URL that took his real order through.

   ⚠️ IT MUST NOT DEFEAT THE MINIMUM-ORDER RULE. That rule blocks by setting
   `pointer-events: none`, so the click never happens at all and this never
   runs. A `disabled` button is skipped for the same reason. The rescue can only
   ever fire on a click the shop already allowed.

   ⚠️ AND IT MUST NOT DOUBLE-NAVIGATE. If the native submit works, the page
   starts unloading and `leaving` is set, so nothing further happens. Worst case
   both fire and both go to the same place.

   It sits ABOVE the ?nhfc=off kill switch on purpose: that switch exists to
   test the free-cans script, and turning that off must never take checkout
   with it.

   ⛔⛔ REMOVED 14 Aug 2026 — IT WAS COSTING B2B BUYERS THEIR PRICES.

   Everything above describes a workaround for a bug that no longer exists. The
   dead checkout button was `assets/bss-custom.js`, left behind by the uninstalled
   BSS app, killing the click in the capture phase. That file stopped loading on
   10 Aug and the owner confirmed: *"yes now it works."* The rescue has had
   nothing to rescue since — but it kept running on every checkout click.

   WHAT IT WAS DOING. `window.location.href = '/checkout'` is a raw GET. The
   cart form's own submit is a POST that carries the session tying the checkout
   to the buyer's COMPANY LOCATION, and the company location is what selects the
   tier catalog. When the GET won the race, the checkout was built without the
   company: the order recorded `purchasingEntity: Customer` and every line was
   charged at RETAIL.

   WHY IT LOOKED RANDOM, AND WHY IT HID FOR FOUR DAYS. It only fired when the
   native submit had not started navigating within 600ms. On a fast connection
   the POST wins, `beforeunload` sets `leaving`, and the rescue never ran — which
   is every correct order: Canafusion 14 Aug 04:05, Tropical Bloom, Cali Goods,
   Isaan Snus. On a slow mobile connection the timer wins. Coffee Inc lost it
   twice in one day, #NH2649379 and #NH2649408, and the owner had to repair both
   orders by hand.

   The `leaving` guard was never sufficient: `beforeunload` does not fire while a
   POST is merely in flight, so a slow submit looks identical to no submit at all.

   ⛔ DO NOT BRING THIS BACK. If checkout ever dies again, find what is eating
   the click — swap the theme to bisect it, as the owner did — and fix THAT.
   Never race the cart form to the checkout.
   ========================================================================= */

/* ============================================================================
   ⭐ TIDY THE COMPANY-SWITCH URL — 10 Aug 2026.

   Shopify's B2B sign-in and company-location switcher bounce the buyer back to
   the shop with ?company_location_changed=true|false stuck on the address:

       nicohub.store/?company_location_changed=true

   It is Shopify's own parameter, not ours — nothing in this theme reads it, so
   nothing is displayed and it just sits there looking like a broken link to
   every partner who signs in. (It is harmless for search: the page already
   declares its canonical as the clean URL, verified 10 Aug.)

   This removes it from the address bar after the page has loaded. replaceState
   rewrites the history entry in place — no reload, no navigation, no effect on
   what was rendered. Any other query the buyer had is preserved.

   ⚠️ Do NOT "fix" this by redirecting: a redirect costs a round trip and would
   throw away the very context Shopify just applied.
   ========================================================================= */
(function () {
  try {
    if (!window.history || !history.replaceState) return;
    var u = new URL(window.location.href);
    if (!u.searchParams.has('company_location_changed')) return;
    u.searchParams.delete('company_location_changed');
    var q = u.searchParams.toString();
    history.replaceState({}, '', u.pathname + (q ? '?' + q : '') + u.hash);
  } catch (e) {}
})();

/* NH FREE CANS — flips cans the buyer already chose from paid to free.
   Loaded by snippets/nh-free-cans.liquid. This is source, not generated.

   ⭐ WHAT IT DOES, IN ONE LINE.
   The server works out how many cans should be free; this makes the cart match.
   It never invents a can and never decides the deal.

   ⛔ WHY IT IS A FILE AND NOT AN INLINE <script> — 7 Aug 2026.
   `render` gives a snippet an isolated scope, so the `unless nh_free_css` guard
   the old inline script sat behind never deduped anything. On the cart page the
   snippet renders twice (drawer + nh-box-nudge section), so the script was
   injected twice and its delegated listener bound twice — every click counted
   double. And the cart section redraws itself through the Section Rendering
   API, where a <script> written in by innerHTML never runs at all. A real asset
   with a bind-once guard and document-level delegation survives both.

   ⛔ THE BUG THAT STARTED THIS. The previous version read
   `root.getAttribute('data-half')` in three places; no markup ever wrote it, so
   the target was always 0. Every + in the old flavour picker clamped back to 0
   ("the + and - dont work"), and `0 === 0` made the Add button enable itself
   reading "Add my 0 free cans" — press it, receive nothing, no error.
   preflight.sh check 3c now fails any script that reads a data- attribute its
   markup does not write.

   THE CONTRACT WITH THE DISCOUNT FUNCTION
   A line counts as free when `properties._nh_free == "1"` AND the vendor is a
   free brand. The function counts only UNMARKED lines toward the rung and caps
   what it zeroes, so marking cans free without the volume earns nothing — the
   marker is a hint, not an entitlement. Which means a miscount here cannot cost
   the buyer money at checkout; it can only make the shop lie, which is worse. */
(function () {
  if (window.__nhFreeCansBound) return;
  window.__nhFreeCansBound = true;

  /* ⭐ THE KILL SWITCH — add ?nhfc=off to any URL and this script does nothing.
     10 Aug 2026. The owner reports the mini cart lagging on every change and I
     cannot reproduce it: signed out I have no prices and cannot even build a
     cart, so every explanation I have offered has been reasoning, not
     measurement, and twice it has been wrong.

     This turns the question into an experiment he can run in one click. Open
     the cart normally, change a quantity, feel it. Then open
     /cart?nhfc=off and do exactly the same thing:

       still slow  -> the cost is the THEME's own cart update, not this script,
                      and no amount of tuning here will fix it
       now fast    -> the cost is this script, and I have a target

     It is deliberately a URL flag and not a setting: it leaves no state behind,
     it cannot be left switched on by accident, and it changes nothing for any
     other buyer. */
  try {
    if (/[?&]nhfc=off\b/i.test(window.location.search)) return;
  } catch (e) {}

  var TRIED_KEY = 'nh_fc_tried';

  /* ⛔ THE RELOAD IS GONE — owner, 9 Aug 2026: *"after adding in minicart it
     reloads the pages automatic and throws you out of the minicart … not
     acceptable lagg"*.

     This used to finish with `window.location.reload()`. It worked, and it was
     the wrong instrument: converting cans is a CART edit, and the theme already
     re-renders the cart from the Section Rendering API without a navigation.
     A full reload re-downloads the document and every asset, loses the buyer's
     scroll position, and — because the drawer is opened by JavaScript, not by a
     URL — closes the mini cart. The buyer added a can and got kicked to the top
     of the page.

     The sections now come back ON the last cart request (`sections` on
     /cart/change.js), so the swap costs no extra round trip at all.

     ⛔ PASS CAP. The reload used to be the accidental backstop: a state that
     never settled produced visible reload thrash. Refreshing in place has no
     such natural limit, so a non-converging state would become a silent tight
     fetch loop. The fingerprint below still blocks an identical retry; this
     caps the non-identical case. It is a backstop, not a mechanism — if it ever
     trips, the state machine is wrong, not the cap. */
  var MAX_PASSES = 8;   /* 12 Sep: 4 was sized for one rung; three rungs can need three passes,
                           and a hand-back can add one. Still a hard page-lifetime ceiling. */
  var passes = 0;

  /* ⛔ ONE CONVERSION AT A TIME, AND NOT ON EVERY KEYSTROKE — 10 Aug 2026.
     Owner: *"the mini cart still lagging a lot, especially if i make any
     adjustment in the tickers"*.

     Every quantity change makes the theme re-render the cart sections, which
     replaces the element this script watches, which fires the observer. Tapping
     + four times therefore started four reconciles, each of them an add, a
     shrink and a re-render, all racing each other over the same cart. The cart
     API serialises them server-side, so they queue up and the drawer sits there
     redrawing itself.

     `busy` means a conversion is already in flight — a second one cannot help,
     because it would be deciding from a cart that is about to change anyway.
     The debounce means a burst of taps produces ONE reconcile after the buyer
     stops, not one per tap. Both are about not asking the question until the
     answer can be stable. */
  var busy = false;
  var pending = null;
  var DEBOUNCE_MS = 350;

  var MAX_WAIT_MS = 1200;
  var firstAsk = 0;

  function scheduleReconcile() {
    var now = +new Date();
    if (!pending) firstAsk = now;
    if (now - firstAsk >= MAX_WAIT_MS) {          /* stop deferring forever */
      if (pending) { clearTimeout(pending); pending = null; }
      firstAsk = 0;
      reconcile();
      return;
    }
    if (pending) clearTimeout(pending);
    pending = setTimeout(function () {
      pending = null; firstAsk = 0; reconcile();
    }, DEBOUNCE_MS);
  }

  function ss(k) { try { return sessionStorage.getItem(k); } catch (e) { return null; } }
  function ssSet(k, v) { try { sessionStorage.setItem(k, v); } catch (e) {} }
  function ssDel(k) { try { sessionStorage.removeItem(k); } catch (e) {} }

  function readState(host) {
    try { return JSON.parse(host.getAttribute('data-state')); } catch (e) { return null; }
  }

  /* ⛔ 12 Sep 2026 — EVERY CART WRITE ON THE SITE GOES THROUGH ONE DOOR NOW.
     This engine and the order page's "Add all to cart" were writing to the cart
     independently, neither aware of the other, neither pausing when the shop
     started refusing. Together they could put twenty odd writes on the wire in a
     few seconds and keep going after the shop had already said stop.

     window.NHCart (assets/nh-cart-guard.js) serialises them, spaces them, and
     holds every write locally once the shop answers 429, so a refusal costs one
     request instead of twenty. The shape returned here is unchanged: something
     with .json() on success, a throw on failure, because that is what the two
     chains below are built on. */
  function post(url, body) {
    if (window.NHCart && typeof window.NHCart.write === 'function') {
      return window.NHCart.write(url, body).then(function (out) {
        if (!out.ok) {
          var err = new Error(url);
          err.throttled = out.throttled;
          throw err;
        }
        return { json: function () { return Promise.resolve(out.body); } };
      });
    }
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body)
    }).then(function (r) { if (!r.ok) throw new Error(url); return r; });
  }

  /* Take `n` cans from lines that are currently free==wantFree, biggest line
     first — fewest splits, and the buyer's cart stays tidy. Returns null if
     there are not enough, in which case nothing is touched. */
  /* ⛔ CHEAPEST FIRST — owner, 10 Aug 2026: *"pablo is discount when killa is in
     the order… should always be the chepeast that goes as discount if they
     mix"*.

     This sorted on QUANTITY alone, so the BIGGEST line won and the choice was
     blind to price. A cart of 300 PABLO and 60 KILLA gave away PABLO at ฿135
     while KILLA sat there at ฿125 — ฿400 handed over on a single order for no
     reason, and, worse, unpredictable: the same buyer got a different brand
     free depending on how they happened to split the order.

     Price ascending fixes both. It costs Nicohub the least on every mixed cart
     (KILLA is the cheaper can to give away and the cheaper can to buy), and it
     makes the deal legible — the free cans are always the cheapest ones, every
     time, so a partner can predict the bill before they reach checkout.

     Quantity still breaks ties, so among equally-priced cans the largest line
     is used first and the cart keeps as few split lines as possible. */
  function take(lines, wantFree, n, dearestFirst) {
    /* ⛔ THE DIRECTION MATTERS, AND IT IS THE SAME RULE BOTH WAYS — 10 Aug 2026.
       Giving cans away: spend the CHEAPEST first.
       Handing surplus back: return the DEAREST first, so the cans that stay
       free are again the cheapest. Sorting ascending in both directions would
       have handed back the cheap KILLA and left the dear PABLO free — the exact
       thing he asked to stop ("should always be the chepeast that goes as
       discount"), just arrived at from the other side. */
    var pool = lines.filter(function (l) { return l.free === wantFree && l.qty > 0; })
                    .sort(function (a, b) {
                      var pa = a.price == null ? 0 : a.price;
                      var pb = b.price == null ? 0 : b.price;
                      if (pa !== pb) return dearestFirst ? pb - pa : pa - pb;
                      return b.qty - a.qty;            /* then fewest splits */
                    });
    var plan = [], left = n;
    for (var i = 0; i < pool.length && left > 0; i++) {
      var cut = tens(Math.min(left, pool[i].qty));   /* tens only, never a 5 */
      if (cut <= 0) continue;
      plan.push({ line: pool[i], cut: cut });
      left -= cut;
    }
    return left > 0 ? null : plan;
  }

  /* ⛔ THE CHOICE IS MADE ONCE, AND THE CART KEEPS CHANGING — owner, 10 Aug
     2026, on a cart holding 30 free KILLA, 10 free PABLO and 10 PAID KILLA
     Melon: *"still take off pablo here"*.

     take() picks the cheapest cans available AT THAT MOMENT, and it did: when
     those cans were marked there were only 30 KILLA in the cart, so 30 KILLA +
     10 PABLO was genuinely the cheapest set. He then added KILLA Melon, and
     nothing ever revisited the decision — so ฿135 PABLO stayed free while ฿125
     KILLA sat there being charged for. Correct when taken, wrong ten seconds
     later.

     improvePlan() re-asks the question against the cart as it is now. It pairs
     the DEAREST free cans against the CHEAPEST paid ones and swaps only while
     the paid can is strictly cheaper — so it always reduces what Nicohub gives
     away, never increases it, and stops as soon as no improvement is left.

     ⛔ THE FREE COUNT NEVER MOVES. This swaps WHICH cans are free, one for one.
     It cannot hand cans back (the fault behind "cant delete the cold mint"),
     and it cannot give more away than the rung allows. Strictly cheaper is
     required, so equal prices are left alone and the plan cannot oscillate. */
  /* ⛔⛔ CANS MOVE IN TENS ONLY — owner, 7 Sep 2026, on a cart showing "KILLA Energy 5 free" and
     "PABLO Dry Ice Cold 5 free": *"found a big error, here its 5 cans in the cart?! we only do 10 by 10"*.
     Every cut made by take(), takeBrand() and improvePlan() is rounded DOWN to a multiple of STEP, and a
     cut that would be smaller than STEP is skipped. The rung (40) is tens already. */
  var STEP = 10;
  function tens(n) { n = Math.floor(n / STEP) * STEP; return n > 0 ? n : 0; }



  function improvePlan(lines) {
    /* ⛔ SAME BRAND ONLY — 7 Sep 2026. Swapping a dear free PABLO for a cheap paid KILLA moved the free count
       between brands (25 KILLA + 15 PABLO on his cart) and broke the fixed 20 + 20 split above. Cheapest-first
       still applies INSIDE each brand, and every swap is a multiple of STEP. */
    var toPaid = [], toFree = [];
    var brands = {};
    lines.forEach(function (l) { var b = (l.brand || '').toString().toUpperCase() || '?'; (brands[b] = brands[b] || []).push(l); });
    Object.keys(brands).sort().forEach(function (b) {
      var dearFree = brands[b].filter(function (l) { return l.free === 1 && l.qty > 0; })
                              .sort(function (x, y) { return (y.price || 0) - (x.price || 0); });
      var cheapPaid = brands[b].filter(function (l) { return l.free === 0 && l.qty > 0; })
                               .sort(function (x, y) { return (x.price || 0) - (y.price || 0); });
      var i = 0, j = 0;
      var leftDear = dearFree.length ? dearFree[0].qty : 0;
      var leftCheap = cheapPaid.length ? cheapPaid[0].qty : 0;
      while (i < dearFree.length && j < cheapPaid.length) {
        if ((cheapPaid[j].price || 0) >= (dearFree[i].price || 0)) break; /* no gain left */
        var n = tens(Math.min(leftDear, leftCheap));
        if (n > 0) {
          toPaid.push({ line: dearFree[i], cut: n });
          toFree.push({ line: cheapPaid[j], cut: n });
          leftDear -= n;
          leftCheap -= n;
        }
        /* a remainder under STEP on either side cannot be swapped: move on */
        if (leftDear < STEP) { i++; leftDear = i < dearFree.length ? dearFree[i].qty : 0; }
        if (leftCheap < STEP) { j++; leftCheap = j < cheapPaid.length ? cheapPaid[j].qty : 0; }
      }
    });
    return (toPaid.length && toFree.length) ? { toPaid: toPaid, toFree: toFree } : null;
  }

  /* ⛔⛔ LET THE THEME REFRESH ITSELF — owner, 10 Aug 2026: *"when making a
     update in popup cart it throw me out from cart and then i cant click on the
     cart. i need to add a new product to cart to be able to see the pop up cart
     again"*.

     That is the signature of markup being replaced underneath Alpine. apply()
     below writes fresh HTML into #CartDrawer and #cart-icon-bubble with
     innerHTML. The new nodes arrive WITHOUT Alpine having bound them, so the
     drawer loses the component that keeps it open (thrown out) and the cart
     icon loses its click handler (cannot reopen). Adding a product recovers it
     because THAT path is the theme's own, and the theme re-binds as it goes.

     The theme already has the routine for this — xMiniCart.reLoad() — and it is
     what runs after every add to cart, which is exactly the path that never
     breaks. Calling it instead of doing our own swap means the cart is refreshed
     the way the theme expects, by the theme, every time.

     apply() stays only as a last resort for when the store is unavailable. */
  function refreshViaTheme() {
    try {
      var mc = window.Alpine && Alpine.store('xMiniCart');
      if (mc && typeof mc.reLoad === 'function') { mc.reLoad(); return true; }
    } catch (e) {}
    return false;
  }

  /* ——— re-rendering the cart in place ———————————————————————————————————
     The theme already owns the list of what has to be redrawn after a cart
     change, and it differs per page (cart page: the item table, the footer and
     the badges; everywhere else: the drawer and the badges). Read ITS list
     rather than keeping a second copy that goes stale the day the theme moves
     a section. If the list cannot be read, targets() returns null and the
     caller reloads. */
  function themeSections() {
    try {
      var s = window.Alpine && Alpine.store('xCartHelper').getSectionsToRender();
      return (s && s.length) ? s.slice() : null;
    } catch (e) { return null; }
  }

  /* ⛔ ALPINE IS NOT THERE YET ON A FRESH PAGE LOAD — measured, 9 Aug 2026.
     theme.js defers its whole bundle behind the first real user interaction
     (touchstart / mouseover / wheel / scroll / keydown), so on the cart page
     `window.Alpine` is undefined at DOMContentLoaded — which is exactly when
     this runs. themeSections() returned null, and the reload fallback fired
     every single time: the page still double-loaded, and my first fix bought
     nothing on the page where the conversion usually happens.
     (In the DRAWER the store is always there — the drawer cannot open without
     Alpine — which is why the mini cart is not the case that needed this.)

     So derive it from the DOM instead, using only element ids the theme's own
     cart code already depends on. Each target's section id comes from its
     enclosing `.shopify-section`, so nothing here hard-codes a section name and
     a renamed or re-templated section keeps working.

     ⛔ ALL OR NOTHING. If the container holding the cart LINES cannot be
     resolved, return null and let the caller reload. Refreshing the badges and
     the message while the line table stays stale is worse than a reload: it
     leaves a cart that contradicts itself. */
  function domSections() {
    var ids = ['main-cart-items', 'main-cart-footer', 'main-cart-upsell',
               'CartDrawer', 'cart-icon-bubble', 'mobile-cart-icon-bubble'];
    var out = [], gotLines = false;
    for (var i = 0; i < ids.length; i++) {
      var el = document.getElementById(ids[i]);
      if (!el || !el.closest) continue;
      var sec = el.closest('.shopify-section');
      if (!sec || !sec.id) continue;
      out.push({ id: sec.id.replace(/^shopify-section-/, ''), selector: '#' + ids[i] });
      if (ids[i] === 'main-cart-items' || ids[i] === 'CartDrawer') gotLines = true;
    }
    return gotLines ? out : null;
  }

  /* ⛔ THE THEME'S LIST DOES NOT INCLUDE OUR OWN SECTION — and on the cart page
     nothing else redraws it, so the free-cans message would keep showing the
     count from before the edit while the table above it showed the new one.
     Rather than hard-code the section's name, take it from the DOM: the host
     always sits inside its own `.shopify-section`, whose id is the section id
     the Section Rendering API answers to. That survives the section being
     renamed, moved, or given a different id by the theme editor. */
  function targets(host) {
    var list = themeSections() || domSections();
    if (!list) return null;
    var own = host.closest ? host.closest('.shopify-section') : null;
    if (own && own.id) {
      var sel = '[id="' + own.id + '"]';
      var dupe = false;
      for (var i = 0; i < list.length; i++) if (list[i].selector === sel) dupe = true;
      if (!dupe) list.push({ id: own.id.replace(/^shopify-section-/, ''), selector: sel });
    }
    return list;
  }

  /* One id per request even though two entries can share one (the theme lists
     quick-order-list twice, under two selectors). */
  function idsOf(list) {
    var out = [];
    for (var i = 0; i < list.length; i++) {
      if (out.indexOf(list[i].id) < 0) out.push(list[i].id);
    }
    return out.join(',');
  }

  /* Returns how many targets were actually swapped — 0 means the caller must
     fall back, never that the job is done. Parsing is done here rather than
     through the theme's own getSectionInnerHTML() because that is a top-level
     `const`: reading it before theme.js has run throws instead of returning
     undefined, and this file is deferred independently of it. */
  function apply(list, map) {
    var n = 0;
    for (var i = 0; i < list.length; i++) {
      var s = list[i];
      var el = document.querySelector(s.selector);
      if (!el || !map[s.id]) continue;
      try {
        var fresh = new DOMParser().parseFromString(map[s.id], 'text/html')
                        .querySelector(s.selector);
        if (fresh) { el.innerHTML = fresh.innerHTML; n++; }
      } catch (e) {}
    }
    return n > 0;
  }

  /* ⛔ A LINE'S PROPERTIES CANNOT BE EDITED IN PLACE. "20 KILLA Cold Mint" and
     "20 KILLA Cold Mint (_nh_free)" are two different cart lines, so converting
     N cans means creating the twin and shrinking the original.

     ADD FIRST, SHRINK SECOND. If the shrink landed first the cart would dip
     below the rung for an instant, and anything reading the cart in that gap —
     a reload, the drawer, another tab — would see an order that no longer
     qualifies and undo the work.

     ⛔ IT MUST NEVER LOOP. Each pass re-renders the cart sections; the fresh
     markup says toMark is 0 and nothing fires. The fingerprint is the backstop
     for a POST that fails silently (a flavour selling out between render and
     click), and MAX_PASSES is the backstop for the fingerprint. */

  /* Convert marked lines back to paid: add the paid twin, then shrink the
     marked line by the same count (a line's properties cannot be edited in
     place). Shared by the surplus hand-back and the wrong-brand hand-back. */
  function handBack(host, backPlan, fp) {
    /* ⛔ THE FINGERPRINT IS WRITTEN ON SUCCESS, NOT BEFORE — 8 Sep 2026. It used
       to be set here, so a hand-back that failed half way (the add went through,
       the shrink did not) left the cart with a line still marked free AND the
       state remembered as "already tried": nothing ever cleaned it up, and the
       buyer kept seeing "Deal: Free with 440 cans" above a line being charged.
       Now a failed pass leaves no fingerprint, so the next tick tries again, and
       MAX_PASSES still stops a loop. */
    busy = true;
    var backSecs = targets(host);
    var backIds = backSecs ? idsOf(backSecs) : null;
    var backChain = post(host.getAttribute('data-add-url'), {
      items: backPlan.map(function (p) { return { id: p.line.id, quantity: p.cut }; })
    });
    backPlan.forEach(function (p, i) {
      var last = i === backPlan.length - 1;
      backChain = backChain.then(function () {
        var body = { id: p.line.key, quantity: p.line.qty - p.cut };
        if (last && backIds) body.sections = backIds;
        return post(host.getAttribute('data-change-url'), body)
                 .then(function (r) { return last ? r.json() : null; });
      });
    });
    backChain.then(function (res) {
      ssSet(TRIED_KEY, fp);                 /* only now: the cans really moved */
      if (refreshViaTheme()) return;
      if (backSecs && res && res.sections) apply(backSecs, res.sections);
    }).catch(function () { ssDel(TRIED_KEY); }).then(function () { busy = false; });
  }

  function reconcile() {
    if (busy) return;
    /* The shop is refusing cart writes. Marking can wait: the lines are already
       in the cart, only the free/paid split is behind, and it is recomputed from
       the cart on the next tick anyway. Spinning here would cost the buyer the
       main thread and cost the shop nothing but more refusals. */
    if (window.NHCart && typeof window.NHCart.cooling === 'function' && window.NHCart.cooling()) return;
    var host = document.querySelector('[data-nh-free-auto]');
    if (!host) return;
    var st = readState(host);
    if (!st) { ssDel(TRIED_KEY); return; }

    /* Two jobs, and only one can be needed at a time.
       MARK  — cans are owed and not yet free (toMark > 0).
       SWAP  — the right NUMBER are free but not the cheapest ones. Only ever
               considered once nothing is owed, so marking always comes first. */
    /* ⛔⛔⛔ THE RE-OPTIMISER IS DISABLED — 10 Aug 2026, SAME NIGHT IT SHIPPED.
       It produced a cart with 50 cans marked free against a cap of 40. The
       discount function zeroed 40 and left the fifth line marked-but-BILLED:
       "KILLA Melon 125.00 ... 1,250.00" with "Free: Yes — taken off at
       checkout" printed underneath it. A line that says free and charges is a
       worse fault than the ฿100 the swap was saving, and it was on the live
       shop.

       improvePlan() itself is proved correct in isolation — its unit tests
       pass and it converges. The defect is in APPLYING it: the add-then-shrink
       pair is written against the cart as it was BEFORE the add, and for a
       two-way swap that assumption does not hold the way it does for a one-way
       marking. I could not test that end to end (signed out I cannot build a
       partner cart), and shipping it on reasoning alone was the mistake.

       Back to mark-only, which was stable. The function is left in place, with
       its tests, for when it can be verified against a real cart. */
    var swap = null;
    /* ⛔ THE WRONG-BRAND HAND-BACK WENT WITH THE TOGGLE — 8 Sep 2026. It existed
       only to undo cans marked under a brand the buyer had since switched away
       from. With no choice there is no wrong brand: every marked can is a KILLA
       or a PABLO, and take() keeps the cheapest ones free. */
    /* ⛔ 11 Sep 2026 — HAND THE BUDGET BACK WHEN THE CART SETTLES. `passes` was never reset,
       so MAX_PASSES was a page-lifetime total. With one rung a buyer spent one pass, maybe two.
       Climbing 440 to 1080 to 1920 in a single session spends three, and the fourth failure left
       the script silently refusing to mark anything for the rest of the page. Counting
       CONSECUTIVE non-converging passes keeps the backstop — a state that never settles never
       reaches this line — while a cart that does converge starts the next rung with a full budget. */
    /* ⛔⛔ 12 Sep 2026 — THE RESET IS REVERTED. Yesterday I changed this to `passes = 0` so a
       three-rung climb would not exhaust a four-pass budget. That turned MAX_PASSES from a hard
       page-lifetime stop into a counter that any oscillation resets, which removes the only thing
       standing between a state that will not converge and an endless post-rerender-post loop.
       Owner today: "it loading forever when trying to add more cans". A backstop that a loop can
       clear is not a backstop. If four passes really are too few for a long climb, the answer is
       a higher MAX_PASSES, not a resettable one. */
    if (!st.toMark) { ssDel(TRIED_KEY); return; }

    var fp = st.cans + '/' + st.earned + '/' + st.targetFree + '/' + st.toMark +
             (swap ? '/swap' + swap.toFree.reduce(function (n, x) { return n + x.cut; }, 0) : '');
    if (ss(TRIED_KEY) === fp) return;
    if (passes >= MAX_PASSES) return;
    passes++;
    busy = true;

    /* ⛔ THIS SCRIPT ONLY EVER GIVES — 10 Aug 2026.
       Owner: *"cant delete the cold mint that is not free, then it updates it
       self and put them back"*.

       `toMark < 0` meant "hand cans back": the cart no longer reaches the rung,
       so un-mark the free ones. The arithmetic was right and the behaviour was
       indefensible. He had 40 paid + 40 free KILLA and deleted the PAID line;
       that dropped the cart to 440, below the 480 the rung needs, so this
       converted his free 40 into paid 40 — and a KILLA line he had just deleted
       reappeared, at full price. The shop undoing a customer's own edit is the
       worst thing a cart can do, and it cost a round trip every time.

       Handing back was never a SAFETY requirement, only tidiness. The discount
       function counts unmarked lines toward the rung and caps what it zeroes,
       so a marked can on an unqualified order earns exactly nothing — being
       marked cannot take money off by itself. Leaving the marks alone is
       therefore free of risk, and the red warning state above already says the
       true thing: those cans would be charged for, here is how many to add to
       earn them back, or remove that line.

       So: mark cans free, never unmark them. The buyer keeps control of their
       own cart, and the only thing that ever changes underneath them is a can
       becoming free. */
    /* ⛔ HAND BACK ONLY THE EXCESS, AND ONLY WHEN THE ORDER QUALIFIES.
       Owner, 10 Aug 2026: *"and this mathemateic is not correct in popup cart"*,
       on a cart carrying 90 marked cans — 30 + 40 + 20 — when the deal gives 40.

       "Never un-mark" was the right answer to him deleting a paid line and
       watching the cans he had just removed reappear. It was too broad. It also
       stopped the script cleaning up a cart holding MORE marked cans than the
       rung can ever pay for, which is a state my own re-optimiser bug created
       and which nothing could then repair. The warning went on to do arithmetic
       on an impossible cart and read like nonsense.

       The two cases are told apart by ONE fact — has the order earned anything:

         earned == 0  the order no longer reaches the rung. Not our business to
                      tidy: this is the delete case, the buyer is mid-edit, and
                      the red warning already explains it. Leave it alone.
         earned  > 0  the order DOES qualify and is simply over-marked. Hand
                      back only the surplus, leaving exactly what was earned.

       So it can never undo a buyer's own edit, and can never leave a cart
       claiming more free cans than the rung allows. */
    if (st.toMark < 0) {
      if (!st.earned) { ssDel(TRIED_KEY); busy = false; return; }
      var giveBack = take(st.lines, 1, -st.toMark, true);  /* DEAREST marked first */
      if (!giveBack) { busy = false; return; }
      handBack(host, giveBack, fp);
      return;
    }

    /* One shape for both jobs: a list of cans to ADD as free, a list to ADD
       back as paid, and the lines each is taken out of. Marking is simply a
       swap with nothing going back. */
    var makeFree = swap ? swap.toFree : take(st.lines, 0, st.toMark);
    var makePaid = swap ? swap.toPaid : [];
    if (!makeFree) { busy = false; return; }
    ssSet(TRIED_KEY, fp);
    var plan = makeFree.concat(makePaid);

    var chain = post(host.getAttribute('data-add-url'), {
      items: plan.map(function (p, idx) {
        var makingFree = idx < makeFree.length;
        var it = { id: p.line.id, quantity: p.cut };
        /* ⛔ TWO PROPERTIES, AND ONLY ONE IS THE MARKER — 9 Aug 2026.
           `_nh_free` is what the discount function reads, and Shopify HIDES any
           property whose key starts with an underscore. That is correct for a
           machine flag, but it meant the cart showed two identical lines —
           "KILLA Mini Cold Mint x40" and "KILLA Mini Cold Mint x60", same name,
           same ฿179 — with nothing saying which one was free. Owner: "it still
           seems a bit complicated to understand this".
           The second key has no underscore, so the storefront, the checkout and
           the packing slip all print it. It is decoration only: never read it,
           never count it. */
        /* ⛔ THE LABEL MUST NOT PROMISE — owner, 10 Aug 2026: *"it cant be
           label like this when someone take our product from the cart"*, over
           three lines reading "Free: Yes — taken off at checkout" while being
           billed ฿6,350.

           It said "Free: Yes — taken off at checkout", which is a claim about
           the OUTCOME. A line property is fixed the moment the line is created
           and cannot be edited afterwards, so the instant the buyer removes
           enough cans to fall below the rung that sentence becomes false — and
           it keeps sitting there on a line the checkout is charging for.

           It states the DEAL instead, which is true in every state: at the rung
           these cans are free, below it they are not, and the wording is the
           same as the discount's own message so the two never disagree. The
           number comes from the cart state, which comes from the generator —
           never typed here. */
        if (makingFree) {
          it.properties = {
            _nh_free: '1'
            /* ⛔ 12 Sep 2026 — THE 'Deal' PROPERTY IS NOT WRITTEN AT ALL NOW. It went stale the
               moment an order grew into a bigger rung (a can marked at 440 still said 440 beside
               a checkout discount saying 1080), and the discount function emits its own message,
               computed live. Both cart renderers also suppress it for lines that still carry one.

               ⛔ AND IT IS OMITTED, NOT SET TO ''. Shopify DROPS an empty property, so a line
               requested as {_nh_free, Deal:''} comes back as {_nh_free} — a different shape from
               the one asked for, which is exactly the kind of mismatch that can make a reconcile
               loop re-fire. Leave the key out. */
          };
        }
        return it;
      })
    });
    /* Ask for the re-rendered cart on the LAST shrink, so the swap below costs
       no request of its own. `sections` is ignored by Shopify when absent. */
    var secs = targets(host);
    var ids = secs ? idsOf(secs) : null;

    plan.forEach(function (p, i) {
      var last = i === plan.length - 1;
      chain = chain.then(function () {
        var body = { id: p.line.key, quantity: p.line.qty - p.cut };
        if (last && ids) body.sections = ids;
        return post(host.getAttribute('data-change-url'), body)
                 .then(function (r) { return last ? r.json() : null; });
      });
    });

    /* ⛔⛔ THE RELOAD IS GONE ENTIRELY — owner, 10 Aug 2026: *"especially if i
       make any adjustment in the tickers, then it reloads, kick me out and then
       cant clikc on caart"*.

       I had kept `window.location.reload()` as the fallback, reasoning that slow
       beats wrong. On the mini cart it is not slow, it is destructive, and it
       does THREE bad things in a row:
         1. the drawer is opened by JavaScript, so a reload closes it;
         2. it throws away the buyer's place mid-edit;
         3. theme.js is deferred until the first real interaction, so on the
            fresh page Alpine is not running yet and the very next click on the
            cart does NOTHING — which is exactly what he reported last.
       A fallback that fires on a quantity change is not a fallback, it is the
       main path, and it was making the cart feel broken.

       So: try the sections that came back on the change request; if those are
       missing, ask for them once more with a GET; and if THAT fails, stop. The
       cart lines are already correct at that point — only the message would be
       stale, and it is corrected by the next render. A stale sentence is a far
       smaller harm than a reload that shuts the drawer and disarms the cart
       button. */
    chain.then(function () {
      if (refreshViaTheme()) return;
      if (secs && ids) {
        return fetch(window.location.pathname + '?sections=' + encodeURIComponent(ids))
          .then(function (r) { return r.json(); })
          .then(function (map) { apply(secs, map); })
          .catch(function () {});
      }
    /* ⛔ 11 Sep 2026 — CLEAR THE FINGERPRINT WHEN THE CART REFUSES.
       ssSet(TRIED_KEY, fp) runs before the POSTs so a concurrent identical retry is blocked
       while one is in flight. But this tail swallowed the rejection without clearing it, so a
       single failed /cart/add.js — a network blip, a sold-out variant — left the fingerprint
       standing and every later tick short-circuited on it. The deal was then dead for the rest
       of the tab session with no message. handBack() was fixed for exactly this on 8 Sep and
       this path was missed. */
    }).catch(function () { ssDel(TRIED_KEY); }).then(function () { busy = false; });
  }
  /* ⛔ THE BRAND TOGGLE IS GONE — 8 Sep 2026, owner: *"it should jsut be the 40
     cheapest of pablo or killa. not that they can choose"*. Nothing writes the
     nh_free_brand cart attribute any more; take() below picks the cheapest cans
     the cart holds among the free brands, so the figure cannot jump when the
     buyer presses a button, and it is always the cheapest set. */

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', reconcile);
  } else {
    reconcile();
  }

  /* The cart page changes quantities without a full page load, so the section
     comes back with a NEW data-state. Only ELEMENT additions are watched —
     reacting to text or style changes would loop. */
  /* ⛔ THE OBSERVER WAS DOING A FULL SUBTREE SCAN PER NODE — 10 Aug 2026.
     Owner: *"pop up cart still lagging everytime i do a change in it"*.

     This ran `el.querySelector('[data-nh-free-auto]')` on EVERY element added
     anywhere in the document. One drawer re-render inserts hundreds of nodes,
     and each of those got its whole subtree walked, synchronously, on the main
     thread — every single time a quantity changed. That is main-thread jank,
     not network time, which is why it was felt on changes that made no requests
     at all.

     None of that work was needed. The observer's only job is "something moved,
     look again later". The looking is already cheap and already debounced:
     reconcile() does one querySelector and one JSON.parse and exits. So the
     handler now just schedules, and the burst of hundreds of mutations from a
     re-render collapses into ONE tick.

     ⚠️ Debouncing alone can starve if mutations never pause — an animation, a
     lazy-loading grid — so scheduleReconcile() also carries a hard ceiling and
     runs regardless once it is reached. */
  if (window.MutationObserver) {
    new MutationObserver(scheduleReconcile)
      .observe(document.documentElement, { childList: true, subtree: true });
  }
})();
